import React, { useState, useRef, useCallback } from 'react';
import '../styles/whats-included.css';

interface Slide {
  category: string;
  title: string;
  description: string;
}

const WhatsIncluded: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const getSlideOffset = useCallback((index: number): number => {
    if (trackRef.current) {
      const card = trackRef.current.querySelector('.included-card') as HTMLElement | null;
      if (card) {
        const gap = window.innerWidth <= 600 ? 12 : window.innerWidth <= 900 ? 16 : 24;
        return index * (card.offsetWidth + gap);
      }
    }
    return index * 944;
  }, []);

  const slides: Slide[] = [
    {
      category: 'Strategic Clarity · Go/No-Go',
      title: 'Should you enter this market?',
      description: 'See if the revenue potential justifies the investment, whether your runway is enough, and whether you can realistically compete. A clear go or no-go before you commit.'
    },
    {
      category: 'Month-by-Month · Revenue',
      title: 'See your revenue grow from day one.',
      description: 'Month by month, watch how your revenue builds from zero to profitability. Modelled on realistic acquisition rates for your target market and industry.'
    },
    {
      category: 'Financial Planning · Costs',
      title: 'Know every cost before you spend a euro.',
      description: 'See when you hire, what each position costs in your target market, and what your total burn rate looks like every single month before you commit.'
    },
    {
      category: 'Market Intelligence · Comparison',
      title: 'Is your target market really the best choice?',
      description: 'We automatically compare your target against multiple similar markets so you can see which ones offer faster profitability, lower costs, and less risk.'
    }
  ];

  const moveCarousel = (direction: number): void => {
    setCurrentSlide(prev => {
      const next = prev + direction;
      if (next < 0) return slides.length - 1;
      if (next >= slides.length) return 0;
      return next;
    });
  };

  const goToSlide = (index: number): void => {
    setCurrentSlide(index);
  };

  return (
    <div className="section" style={{paddingTop: '24px'}}>
      <div style={{textAlign:'center', marginBottom: '32px'}}>
        <div className="delivery-badge">
          <div className="dot"></div>
          <span>Simulation typically delivered within 72 hours</span>
        </div>
      </div>
      <div style={{marginTop:'32px'}} className="section-label"><span>What's Included In Your Free Simulation</span></div>
      <h2 className="section-title">Your expansion, fully simulated<br/><em>across four dimensions.</em></h2>
      <p className="section-sub">Unlike market research reports, we simulate exactly how your expansion will most likely unfold month by month across your first 12 months in a new market.</p>

      <div className="carousel-wrapper">
        <div className="carousel-viewport">
          <div
            className="carousel-track"
            ref={trackRef}
            style={{transform: `translateX(-${getSlideOffset(currentSlide)}px)`}}
            id="carouselTrack"
          >
            {slides.map((slide, index) => (
              <div key={index} className="included-card">
                <div className="included-visual mockup-visual">
                  <div className="mockup-embed" style={index === 3 ? {background:'var(--cream)'} : {}}>
                    {index === 0 && (
                      <>
                        <div className="me-nav">
                          <div className="me-tab active">Overview</div>
                          <div className="me-tab">Simulation</div>
                          <div className="me-tab">Costs</div>
                          <div className="me-tab">Risks</div>
                        </div>
                        <div className="me-verdict">
                          <div className="me-score">
                            <svg viewBox="0 0 62 62" width="48" height="48">
                              <circle cx="31" cy="31" r="26" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4"/>
                              <circle cx="31" cy="31" r="26" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="4" strokeLinecap="round" strokeDasharray="164" strokeDashoffset="45" style={{transform:'rotate(-90deg)',transformOrigin:'center'}}/>
                            </svg>
                            <div className="me-score-num">7<small>/10</small></div>
                          </div>
                          <div className="me-verdict-text">
                            <span className="me-vt-label">Entry Score</span>
                            <span className="me-vt-title">Conditional GO</span>
                            <span className="me-vt-sub">Germany · Berlin · EdTech</span>
                          </div>
                        </div>
                        <div className="me-scenarios">
                          <div className="me-sc worst">
                            <span className="me-sc-tag worst">Worst Case</span>
                            <span className="me-sc-amount">$118K</span>
                            <span className="me-sc-net negative">Net −$223K</span>
                          </div>
                          <div className="me-sc base">
                            <span className="me-sc-tag base">Base Case</span>
                            <span className="me-sc-amount">€353K</span>
                            <span className="me-sc-net positive">Net +$12K</span>
                          </div>
                          <div className="me-sc best">
                            <span className="me-sc-tag best">Best Case</span>
                            <span className="me-sc-amount">$706K</span>
                            <span className="me-sc-net positive">Net +$365K</span>
                          </div>
                        </div>
                        <div className="me-kpis">
                          <div className="me-kpi"><span className="me-kpi-val amber">23%</span><span className="me-kpi-name">Tax</span></div>
                          <div className="me-kpi"><span className="me-kpi-val green">€5B</span><span className="me-kpi-name">Funding</span></div>
                          <div className="me-kpi"><span className="me-kpi-val">$2.6M</span><span className="me-kpi-name">SOM</span></div>
                          <div className="me-kpi"><span className="me-kpi-val green">3-4mo</span><span className="me-kpi-name">Cycle</span></div>
                        </div>
                      </>
                    )}
                    {index === 1 && (
                      <>
                        <div className="me-nav">
                          <div className="me-tab">Overview</div>
                          <div className="me-tab active">Simulation</div>
                          <div className="me-tab">Costs</div>
                          <div className="me-tab">Risks</div>
                        </div>
                        <div className="me-chart-header">
                          <div className="me-ch-eyebrow">Cumulative Revenue vs Costs</div>
                          <div className="me-ch-row">
                            <span className="me-ch-total">$353.0K</span>
                            <span className="me-ch-badge">+$12K profit</span>
                          </div>
                        </div>
                        <div className="me-chart-legend">
                          <div className="me-legend-item"><span className="me-legend-dot rev"></span>Revenue</div>
                          <div className="me-legend-item"><span className="me-legend-dot cost"></span>Costs</div>
                          <div className="me-legend-item"><span className="me-legend-sq loss"></span>Loss period</div>
                          <div className="me-legend-item"><span className="me-legend-sq profit"></span>Profit period</div>
                        </div>
                        <div className="me-chart-area">
                          <svg viewBox="0 0 432 150" fill="none">
                            <line x1="40" y1="10" x2="420" y2="10" stroke="#d4cfc4" strokeWidth="0.3"/>
                            <line x1="40" y1="40" x2="420" y2="40" stroke="#d4cfc4" strokeWidth="0.3"/>
                            <line x1="40" y1="70" x2="420" y2="70" stroke="#d4cfc4" strokeWidth="0.3"/>
                            <line x1="40" y1="100" x2="420" y2="100" stroke="#d4cfc4" strokeWidth="0.3"/>
                            <line x1="40" y1="130" x2="420" y2="130" stroke="#d4cfc4" strokeWidth="0.3"/>
                            <text x="36" y="13" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="end">€350k</text>
                            <text x="36" y="43" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="end">€280k</text>
                            <text x="36" y="73" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="end">€210k</text>
                            <text x="36" y="103" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="end">€140k</text>
                            <text x="36" y="133" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="end">€70k</text>
                            <polygon points="72,102 103,94 135,86 166,78 198,70 230,62 261,54 293,46 324,38 340,33 340,33 324,42 293,55 261,70 230,85 198,100 166,115 135,130 103,130 72,130" fill="#a01a1a" opacity="0.06"/>
                            <polygon points="340,33 356,30 387,22 420,14 420,10 387,14 356,22 340,33" fill="#2d6a4a" opacity="0.08"/>
                            <polyline points="72,130 103,130 135,130 166,115 198,100 230,85 261,70 293,55 324,42 356,22 387,14 420,10" stroke="#2d6a4a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="72,102 103,94 135,86 166,78 198,70 230,62 261,54 293,46 324,38 356,30 387,22 420,14" stroke="#a01a1a" strokeWidth="1.5" strokeDasharray="5,4" opacity="0.55"/>
                            <circle cx="340" cy="33" r="5" fill="#2d6a4a"/>
                            <circle cx="340" cy="33" r="9" fill="none" stroke="#2d6a4a" strokeWidth="1" opacity="0.2"/>
                            <rect x="290" y="-4" width="80" height="26" rx="3" fill="#1a3a2a"/>
                            <text x="330" y="9" fontFamily="Libre Baskerville, serif" fontSize="10" fill="#fff" textAnchor="middle" fontWeight="700">€284K</text>
                            <text x="330" y="18" fontFamily="DM Mono, monospace" fontSize="6" fill="rgba(255,255,255,0.4)" textAnchor="middle">break-even · M10</text>
                            <line x1="166" y1="8" x2="166" y2="130" stroke="#b5600a" strokeWidth="0.6" strokeDasharray="2,3" opacity="0.4"/>
                            <text x="170" y="10" fontFamily="DM Mono, monospace" fontSize="6.5" fill="#b5600a" opacity="0.6">first revenue</text>
                            <text x="72" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">Jan</text>
                            <text x="135" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">Mar</text>
                            <text x="198" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">May</text>
                            <text x="261" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">Jul</text>
                            <text x="324" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">Sep</text>
                            <text x="387" y="143" fontFamily="DM Mono, monospace" fontSize="7" fill="#8a8680" textAnchor="middle">Nov</text>
                          </svg>
                        </div>
                        <div className="me-chart-footer">
                          <div className="me-cf-text">Modeled on your actual sales cycle, pricing, and hiring plan.</div>
                          <div className="me-cf-date">Jan – Dec 2027</div>
                        </div>
                      </>
                    )}
                    {index === 2 && (
                      <>
                        <div className="me-nav">
                          <div className="me-tab">Overview</div>
                          <div className="me-tab">Simulation</div>
                          <div className="me-tab active">Costs</div>
                          <div className="me-tab">Risks</div>
                        </div>
                        <div className="me-cost-header">
                          <div>
                            <div className="me-ch-eyebrow">Monthly Burn Rate</div>
                            <div className="me-cost-total-big">$28,400</div>
                            <div className="me-cost-total-sub">€340,800 / year · all positions filled</div>
                          </div>
                          <div className="me-cost-period-tabs">
                            <div className="me-cp-tab">Daily</div>
                            <div className="me-cp-tab">Weekly</div>
                            <div className="me-cp-tab active">Monthly</div>
                            <div className="me-cp-tab">Yearly</div>
                          </div>
                        </div>
                        <div className="me-cost-items">
                          <div className="me-cost-item">
                            <div className="me-ci-indicator staff"></div>
                            <div className="me-ci-content">
                              <div className="me-ci-top">
                                <div className="me-ci-name">Staffing · 4 positions</div>
                                <div className="me-ci-amount">$17,000</div>
                              </div>
                              <div className="me-ci-detail">Country Manager $4,800 · Sales $4,200 · Marketing $3,600 · Ops $3,200</div>
                              <div className="me-ci-bar"><div className="me-ci-bar-fill staff" style={{width:'60%'}}></div></div>
                            </div>
                          </div>
                          <div className="me-cost-item">
                            <div className="me-ci-indicator tax"></div>
                            <div className="me-ci-content">
                              <div className="me-ci-top">
                                <div className="me-ci-name">Employer Taxes & Social</div>
                                <div className="me-ci-amount">$3,910</div>
                              </div>
                              <div className="me-ci-detail">23% on gross · CM $1,104 · Sales $966 · Marketing $828 · Ops $736</div>
                              <div className="me-ci-bar"><div className="me-ci-bar-fill tax" style={{width:'14%'}}></div></div>
                            </div>
                          </div>
                          <div className="me-cost-item">
                            <div className="me-ci-indicator ops"></div>
                            <div className="me-ci-content">
                              <div className="me-ci-top">
                                <div className="me-ci-name">Ops, Insurance & Marketing</div>
                                <div className="me-ci-amount">$7,490</div>
                              </div>
                              <div className="me-ci-detail">Ad spend $3,200 · SaaS & tools $1,400 · travel $1,600 · D&O insurance $800</div>
                              <div className="me-ci-bar"><div className="me-ci-bar-fill ops" style={{width:'26%'}}></div></div>
                            </div>
                          </div>
                        </div>
                        <div className="me-cost-footer">
                          <div className="me-cost-footer-note">Costs ramp gradually as each hire starts. Full burn rate reached by Month 10.</div>
                          <div className="me-hire-timeline">
                            <div className="me-ht-dot h1">CM</div>
                            <div className="me-ht-dot h2">SA</div>
                            <div className="me-ht-dot h3">MK</div>
                            <div className="me-ht-dot h4">OP</div>
                          </div>
                        </div>
                      </>
                    )}
                    {index === 3 && (
                      <>
                        <div className="me-nav">
                          <div className="me-tab">Overview</div>
                          <div className="me-tab">Simulation</div>
                          <div className="me-tab">Costs</div>
                          <div className="me-tab active">Compare</div>
                        </div>
                        <div style={{padding:'12px 16px 4px'}}>
                          <div className="me-ch-eyebrow">Multi-Market Comparison</div>
                          <div style={{fontFamily:"'Libre Baskerville',serif",fontSize:'13px',fontWeight:700,color:'var(--text-dark)',margin:'4px 0 10px'}}>Germany vs. Alternatives</div>
                        </div>
                        <div style={{padding:'0 16px 12px'}}>
                          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px'}}>
                            <thead>
                              <tr style={{borderBottom:'1px solid rgba(0,0,0,0.06)'}}>
                                <th style={{textAlign:'left',padding:'0 0 6px',fontSize:'7px',fontWeight:500,letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)'}}>#</th>
                                <th style={{textAlign:'left',padding:'0 0 6px',fontSize:'7px',fontWeight:500,letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)'}}>Market</th>
                                <th style={{textAlign:'left',padding:'0 0 6px',fontSize:'7px',fontWeight:500,letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)'}}>Score</th>
                                <th style={{textAlign:'left',padding:'0 0 6px',fontSize:'7px',fontWeight:500,letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)'}}>Profitable</th>
                                <th style={{textAlign:'left',padding:'0 0 6px',fontSize:'7px',fontWeight:500,letterSpacing:'0.1em',textTransform:'uppercase' as const,color:'var(--text-light)'}}>Verdict</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr style={{background:'rgba(124,154,110,0.06)',borderBottom:'1px solid rgba(0,0,0,0.03)'}}>
                                <td style={{padding:'7px 0',fontWeight:600,color:'var(--forest)'}}>1</td>
                                <td style={{padding:'7px 0',fontWeight:600,color:'var(--forest)'}}>🇳🇱 NL</td>
                                <td style={{padding:'7px 0',color:'var(--sage)',fontWeight:600}}>8.4</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>Mo 7</td>
                                <td style={{padding:'7px 0'}}><span style={{fontSize:'8px',fontWeight:600,background:'rgba(124,154,110,0.15)',color:'var(--sage)',padding:'2px 5px',borderRadius:'3px'}}>Recommended</span></td>
                              </tr>
                              <tr style={{borderBottom:'1px solid rgba(0,0,0,0.03)'}}>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>2</td>
                                <td style={{padding:'7px 0',color:'var(--text-dark)',fontWeight:500}}>🇩🇰 DK</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>7.8</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>Mo 8</td>
                                <td style={{padding:'7px 0'}}><span style={{fontSize:'8px',fontWeight:600,background:'rgba(124,154,110,0.12)',color:'var(--sage)',padding:'2px 5px',borderRadius:'3px'}}>GO</span></td>
                              </tr>
                              <tr style={{borderBottom:'1px solid rgba(0,0,0,0.03)'}}>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>3</td>
                                <td style={{padding:'7px 0',color:'var(--text-dark)',fontWeight:500}}>🇩🇪 DE</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>7.0</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>Mo 10</td>
                                <td style={{padding:'7px 0'}}><span style={{fontSize:'8px',fontWeight:600,background:'rgba(196,180,154,0.2)',color:'#8a7a60',padding:'2px 5px',borderRadius:'3px'}}>Conditional</span></td>
                              </tr>
                              <tr>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>4</td>
                                <td style={{padding:'7px 0',color:'var(--text-dark)',fontWeight:500}}>🇵🇱 PL</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>6.5</td>
                                <td style={{padding:'7px 0',color:'var(--text-mid)'}}>Mo 11</td>
                                <td style={{padding:'7px 0'}}><span style={{fontSize:'8px',fontWeight:600,background:'rgba(196,180,154,0.2)',color:'#8a7a60',padding:'2px 5px',borderRadius:'3px'}}>Conditional</span></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="included-body">
                  <div className="cat">{slide.category}</div>
                  <h3>{slide.title}</h3>
                  <p>{slide.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="carousel-controls">
        <button className="carousel-btn" onClick={() => moveCarousel(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <div className="carousel-dots">
          {slides.map((_, index) => (
            <div key={index} className={`carousel-dot ${currentSlide === index ? 'active' : ''}`} onClick={() => goToSlide(index)}></div>
          ))}
        </div>
        <button className="carousel-btn" onClick={() => moveCarousel(1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default WhatsIncluded;
